/**
 * A run on the canvas (Feature 71, 7.3): the graph it started on, arranged
 * by depth, since a run keeps no layout of its own, with each step carrying
 * where it stands and each gate what it decided (Feature 72 D11). Built on
 * the editor's `toFlow`, so a run and its template draw alike.
 *
 * A gate node (Feature 79 D23, D34) says what it did, the branch it took,
 * none held, or skipped, from the run's `decisions`; its condition node taken
 * is marked and the others read not taken, as do the links after them, whose
 * steps are skipped.
 */
import type {
  LinkageRun,
  LinkageRunDecision,
  LinkageStepStatus,
  TaskLinkage,
} from '@optum/api-client/types';

import {
  type Draft,
  draftLinks,
  FIRST,
  type FlowEdge,
  type FlowNode,
  type ReturnEdge,
} from './draft.js';
import { parseConditionId } from './conditions.js';
import {
  toFlow,
  type ConditionFlowNode,
  type GateFlowNode,
  type Tie,
  type WayIn,
} from './to-flow.js';
import { CALL, COMPLETION } from './start.js';
import { rootsOf } from './modes.js';

// What a run's first node names when no first task tells its capability.
const UNKNOWN_CAPABILITY = '—';
// The canvas grows with the tallest column, within these bounds.
const MIN_HEIGHT = 200;
const MAX_HEIGHT = 420;
const NODE_ROOM = 170;
// The fit centres the nodes, so this is split above and below them.
const RETURN_ROOM = 80;

/**
 * Where a node stands: a step's status, or `first`, the task the run started
 * from. A type, not an interface, so it stays assignable to a node's data.
 */
export type RunNodeState = {
  status: LinkageStepStatus | 'first';
  reason: string | null;
  failureReason: string | null;
  taskId: string | null;
  /** On a schedule run's `first`: the slot it fired for (Feature 77 D17). */
  firedFor?: string | null | undefined;
  /** Outside the subtree a page is about: drawn faint. */
  dimmed: boolean;
};

export interface RunFlowNode extends Omit<FlowNode, 'data'> {
  data: FlowNode['data'] & RunNodeState;
}

/** Where a run's gate stands, and whether it is outside the subtree a page is about. */
export type RunGateState = Pick<LinkageRunDecision, 'status' | 'branch' | 'reason'> & {
  dimmed: boolean;
};

export interface RunGateFlowNode extends Omit<GateFlowNode, 'data'> {
  data: GateFlowNode['data'] & RunGateState;
}

/** A condition node on a run: taken, not taken, or its gate not decided yet. */
export type ConditionState = 'taken' | 'not_taken' | 'open';

export interface RunConditionFlowNode extends Omit<ConditionFlowNode, 'data'> {
  data: ConditionFlowNode['data'] & { state: ConditionState; dimmed: boolean };
}

/** A run's graph as a draft, so the editor's own drawing and modes read it. */
function draftOf(run: LinkageRun, firstCapabilityId: string): Draft {
  return {
    key: run.template_key,
    name: run.template_key,
    description: null,
    // A run always draws its first node, even a refused fire's (Feature 80 D11).
    firstCapabilityId: firstCapabilityId || UNKNOWN_CAPABILITY,
    // A schedule run's `first` is its fire; the cron is not kept on the run. A
    // call run's `first` is the call, drawn with its fork (Feature 78 D8).
    start:
      run.start_kind === 'schedule'
        ? { kind: 'schedule', cron: null }
        : run.start_kind === 'call'
          ? CALL
          : COMPLETION,
    steps: run.graph.steps,
    links: draftLinks(run.graph.links),
    decisions: run.graph.decisions ?? [],
    layout: {},
  };
}

/** A root's subtree in a run: the root and every step whose root it is. */
export function subtreeOf(run: LinkageRun, root: string): Set<string> {
  const roots = rootsOf(draftOf(run, ''));
  return new Set([root, ...[...roots].filter(([, r]) => r === root).map(([name]) => name)]);
}

function stateOf(run: LinkageRun, id: string): Omit<RunNodeState, 'dimmed'> {
  if (id === FIRST) {
    const firedFor = run.start_kind === 'schedule' ? (run.scheduled_for ?? null) : undefined;
    // A fire whose first task could not be submitted (Feature 80 D11).
    if (run.first_failure) {
      const failure = run.first_failure;
      return { status: 'failed', reason: failure, failureReason: failure, taskId: null, firedFor };
    }
    return { status: 'first', reason: null, failureReason: null, taskId: null, firedFor };
  }
  const step = run.steps.find((s) => s.name === id);
  return {
    status: step?.status ?? 'waiting',
    reason: step?.reason ?? null,
    failureReason: step?.failure_reason ?? null,
    taskId: step?.task_id ?? null,
  };
}

/**
 * The run's nodes, links, and write-backs, `first` standing for the task whose
 * completion started it. With `focus`, an independent step's name, every node
 * outside its subtree is dimmed: the page is about that subtree.
 */
export function runFlow(
  run: LinkageRun,
  firstCapabilityId: string,
  focus?: string | undefined,
): {
  nodes: RunFlowNode[];
  gates: RunGateFlowNode[];
  conditions: RunConditionFlowNode[];
  ways: WayIn[];
  ties: Tie[];
  edges: FlowEdge[];
  returns: ReturnEdge[];
} {
  const { nodes, gates, conditions, ways, ties, edges, returns } = toFlow(
    draftOf(run, firstCapabilityId),
  );
  const inFocus = focus === undefined ? null : subtreeOf(run, focus);
  const decided = (gate: string) => run.decisions?.find((d) => d.name === gate);
  return {
    gates: gates.map((node) => {
      const state = decided(node.id);
      return {
        ...node,
        data: {
          ...node.data,
          status: state?.status ?? 'waiting',
          branch: state?.branch ?? null,
          reason: state?.reason ?? null,
          dimmed: inFocus !== null && !inFocus.has(node.id),
        },
      };
    }),
    conditions: conditions.map((node) => {
      const state = decided(node.data.gate);
      // A skipped gate took no way out; a waiting one has not chosen yet.
      const taken: ConditionState =
        state?.status === 'decided'
          ? state.branch === node.data.branch
            ? 'taken'
            : 'not_taken'
          : state?.status === 'skipped'
            ? 'not_taken'
            : 'open';
      return {
        ...node,
        data: {
          ...node.data,
          state: taken,
          dimmed: inFocus !== null && !inFocus.has(node.data.gate),
        },
      };
    }),
    ways,
    ties,
    nodes: nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        ...stateOf(run, node.id),
        dimmed: inFocus !== null && !inFocus.has(node.id),
      },
    })),
    // A gate shows its outcome once the step behind it was decided, and what
    // its list tests counted when it read its rules (Feature 77 D24).
    edges: edges.map((edge) => {
      const step = run.steps.find((s) => s.name === edge.target);
      // A branch's link leaves its condition node; its step records it by its gate.
      const from = parseConditionId(edge.source)?.gate ?? edge.source;
      return {
        ...edge,
        data: {
          ...edge.data,
          outcome: step?.gates?.[from],
          lists: step?.list_tests?.[from],
        },
      };
    }),
    returns,
  };
}

/**
 * Enough height for the tallest column, within bounds, and room below it for
 * the write-backs' arrows, which run under every node.
 */
export function canvasHeight(
  nodes: readonly { position: { y: number } }[],
  hasReturns = false,
): number {
  const ys = nodes.map((n) => n.position.y);
  const span = ys.length > 0 ? Math.max(...ys) - Math.min(...ys) : 0;
  const room = span + NODE_ROOM + (hasReturns ? RETURN_ROOM : 0);
  return Math.min(MAX_HEIGHT + RETURN_ROOM, Math.max(MIN_HEIGHT, room));
}

/** The run a linked task belongs to: the one whose steps submitted it. */
export function runOf(linkage: TaskLinkage, taskId: string): LinkageRun | undefined {
  return linkage.runs.find((run) => run.steps.some((s) => s.task_id === taskId));
}

/** "1 of 2 runs open", or "2 runs, ended". */
/** How a run started, so a task with runs of each kind reads them apart (Feature 78 D20). */
export function runStartWords(run: LinkageRun): string {
  if (run.start_kind === 'call') return 'on the call';
  if (run.start_kind === 'schedule') return 'on a schedule';
  return 'after it completed';
}

export function runsText(runs: readonly LinkageRun[]): string {
  const open = runs.filter((r) => r.status === 'open').length;
  const noun = runs.length === 1 ? 'run' : 'runs';
  return open > 0 ? `${open} of ${runs.length} ${noun} open` : `${runs.length} ${noun}, ended`;
}
